import { useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import type { FileRejection } from "react-dropzone";

import { toastError, toastSuccess } from "@alpacahq/ui/old";

import {
  useGetCurrentAccountIfLiveElseTradingAccount,
  useMutationRequest,
} from "~/v2/api/hooks";
import { queryKeys } from "~/v2/api/queryKeys";
import {
  type DocumentRequestResponse,
  uploadAccountDocument,
  uploadAccountDocumentByDocType,
} from "~/v2/api/rest/account";
import {
  FILE_ACCEPTED_TYPES,
  FILE_MAX_SIZE,
  FILE_SIZE_ERR_MSG,
  FILE_TYPE_ERR_MSG,
} from "~/v2/components/fileUpload";
import { getRequestedDocumentContent } from "./RequestedDocument.utils";

export const useRequestedDocumentUpload = (
  isLegalEntity: boolean,
  documentRequest: DocumentRequestResponse,
) => {
  // TODO (MLA): Review UX
  const { account } = useGetCurrentAccountIfLiveElseTradingAccount();
  const queryClient = useQueryClient();
  const [selectedDocType, setSelectedDocType] = useState(
    documentRequest?.doc_sub_type ?? "",
  );
  const [file, setFile] = useState<{ id: string; name: string } | null>(null);

  const content = getRequestedDocumentContent(isLegalEntity, documentRequest);

  const { mutate, isPending } = useMutationRequest(
    (file: File) => {
      const docType = documentRequest?.doc_type ?? "";
      if (documentRequest) {
        return uploadAccountDocument(documentRequest.id, {
          doc_type: docType,
          doc_sub_type: selectedDocType || "",
          content: file,
        });
      } else {
        return uploadAccountDocumentByDocType(account?.id || "", {
          doc_type: docType,
          doc_sub_type: selectedDocType || "",
          content: file,
        });
      }
    },
    {
      onSuccess: () => {
        toastSuccess(`${content?.title} uploaded successfully.`);
        queryClient.invalidateQueries({
          queryKey: queryKeys.documents.requests(account?.id ?? ""),
        });
      },
      onError: (err) => {
        const error = err as AxiosError;
        if (error.response?.status === 413) {
          toastError(FILE_SIZE_ERR_MSG);
        } else if (error.response?.status === 415) {
          toastError(FILE_TYPE_ERR_MSG);
        } else if (error.response?.status === 404) {
          toastError("Account not found");
        } else {
          toastError(`Error uploading file: ${error.message}`);
        }
      },
    },
  );

  const onUpload = (file: File) => {
    if (
      !selectedDocType &&
      content?.selectOptions &&
      content?.selectOptions?.length > 0
    ) {
      toastError("Please select a document type first");
      return;
    }

    if (!FILE_ACCEPTED_TYPES.includes(file.type)) {
      toastError(FILE_TYPE_ERR_MSG);
      return;
    }

    if (file.size > FILE_MAX_SIZE) {
      toastError(FILE_SIZE_ERR_MSG);
      return;
    }

    mutate(file);
  };

  // Ensure the value we pass to SingleSelect exists in options
  const validatedValue =
    content?.selectOptions?.find(({ value }) => value === selectedDocType)
      ?.value || "";

  const onError = (fileRejection: FileRejection) => {
    const error = fileRejection.errors[0];
    if (error.code === "file-too-large") {
      toastError(FILE_SIZE_ERR_MSG);
    } else if (error.code === "file-invalid-type") {
      toastError(FILE_TYPE_ERR_MSG);
    } else {
      toastError(`Error: ${error.message}`);
    }
  };

  const onRemove = () => {
    setFile(null);
  };

  const handleSelectedDocTypeChange = (value: string) => {
    setSelectedDocType(
      (value || "") as DocumentRequestResponse["doc_sub_type"],
    );
  };

  return {
    onSelectedDocTypeChange: handleSelectedDocTypeChange,
    onUpload,
    onError,
    onRemove,
    file,
    selectedDocType,
    validatedValue,
    isLoading: isPending,
    content,
  };
};
